import React, { useEffect, useState } from "react";
import { Box, Typography, MenuItem, Select, Button, CircularProgress } from "@mui/material";
import { fetchMyWorkspaces } from "../../services/shopifyApi";

/**
 * Shared "select a workspace to connect this Shopify store to" step — used by both
 * Welcome.jsx (already-logged-in case) and Login.jsx (post-login case). Matches the
 * pattern confirmed against BotSpace's own "Select a workspace" screen this session.
 *
 * Fetches the account's workspaces and filters out any already connected to a
 * different Shopify store (the backend's collision guard would just soft-no-op one of
 * those anyway — filtering here avoids offering a choice that would silently do
 * nothing). Auto-confirms immediately if exactly one workspace remains — no reason to
 * make someone click Continue when there's nothing to actually choose between.
 */
const ShopifyWorkspacePicker = ({ token, mode, onConfirm, onError }) => {
  const [workspaces, setWorkspaces] = useState(null); // null = still loading
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchMyWorkspaces(token);
        if (cancelled) return;
        const pickable = (all || []).filter((ws) => !ws.shopifyConnected);
        setWorkspaces(pickable);
        if (pickable.length === 1) {
          setSelected(pickable[0]._id);
        }
      } catch (err) {
        if (!cancelled) onError?.(err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (workspaces && workspaces.length === 1 && selected) {
      onConfirm(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces]);

  if (workspaces === null || (workspaces.length === 1 && selected)) {
    // Loading, or the single-workspace case auto-confirming via the effect above —
    // either way there's nothing to render but a brief spinner.
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} sx={{ color: accentColor }} />
      </Box>
    );
  }

  if (workspaces.length === 0) {
    return (
      <Typography sx={{ fontSize: "14px", color: mode === "dark" ? "#9ca3af" : "#6b7280" }}>
        Every workspace on your account is already connected to a Shopify store.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          mb: 1,
          color: mode === "dark" ? "#f5f5f5" : "#121a34",
          fontFamily: "Instrument Sans, sans-serif",
        }}
      >
        Select a workspace to connect to Shopify
      </Typography>
      <Select
        fullWidth
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        displayEmpty
        sx={{ mb: 2, borderRadius: "12px" }}
      >
        <MenuItem value="" disabled>
          Choose a workspace
        </MenuItem>
        {workspaces.map((ws) => (
          <MenuItem key={ws._id} value={ws._id}>
            {ws.name}
          </MenuItem>
        ))}
      </Select>
      <Button
        fullWidth
        variant="contained"
        disabled={!selected || submitting}
        onClick={async () => {
          setSubmitting(true);
          await onConfirm(selected);
        }}
        sx={{
          py: 1.5,
          backgroundColor: accentColor,
          color: "#ffffff",
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 700,
          fontFamily: "Instrument Sans, sans-serif",
        }}
      >
        {submitting ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Continue"}
      </Button>
    </Box>
  );
};

export default ShopifyWorkspacePicker;
